/** @format */

import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/atom-one-dark.css";
import {
  MessageSquare,
  Plus,
  X,
  Send,
  Paperclip,
  User,
  Moon,
  Sun,
  LogOut,
  Zap,
  CheckCircle,
  AlertCircle,
  Clock,
  Copy,
  MoreVertical,
  Download,
  FileText,
  File as FileIcon,
  Image as ImageIcon,
  Square,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
// ==================== Configuration & Constants ====================

const STORAGE_KEYS = {
  theme: "chat_ui_theme",
  chats: "chat_ui_chats",
  activeChatId: "chat_ui_active_chat_id",
};

const MAX_FILE_SIZE_MB = 25;
const ACCEPTED_TYPES = [
  "image/",
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
];

// ==================== Utility Functions ====================

/**
 * Convert markdown to plain text for copying
 * Preserves formatting like code blocks, lists, etc. in a readable plain text format
 */
function markdownToPlainText(markdown) {
  if (!markdown) return "";

  let text = markdown;

  // Preserve code blocks with proper formatting
  text = text.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    return `\n${code.trim()}\n`;
  });

  // Remove inline code backticks but keep the content
  text = text.replace(/`([^`]+)`/g, "$1");

  // Remove horizontal rules (---, ***, ___)
  text = text.replace(/^[-*_]{3,}\s*$/gm, "");

  // Convert headers to plain text (remove all # symbols at start of line)
  text = text.replace(/^#+\s+/gm, "");

  // Convert bold/italic to plain text (remove ** __ * _)
  text = text.replace(/\*\*(.+?)\*\*/g, "$1");
  text = text.replace(/\*(.+?)\*/g, "$1");
  text = text.replace(/__(.+?)__/g, "$1");
  text = text.replace(/_(.+?)_/g, "$1");

  // Convert links to plain text with URL
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");

  // Convert unordered lists to plain text with bullets
  text = text.replace(/^\s*[-*+]\s+/gm, "• ");

  // Keep numbered lists as is (they're already readable)
  // text = text.replace(/^\s*\d+\.\s+/gm, (match) => match);

  // Remove blockquote markers
  text = text.replace(/^>\s+/gm, "");

  // Clean up multiple consecutive newlines (keep max 2)
  text = text.replace(/\n{3,}/g, "\n\n");

  // Trim whitespace from each line
  text = text
    .split("\n")
    .map((line) => line.trim())
    .join("\n");

  // Remove leading/trailing whitespace
  return text.trim();
}

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : initialValue;
    } catch (err) {
      console.warn(`Failed to read from localStorage key "${key}":`, err);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      console.warn(`Failed to write to localStorage key "${key}":`, err);
    }
  }, [key, value]);

  return [value, setValue];
}

function formatFileSize(bytes) {
  if (!bytes) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

function getFileIcon(fileType) {
  if (!fileType) return <FileIcon className="w-4 h-4 text-gray-600" />;
  if (fileType.startsWith("image/"))
    return <ImageIcon className="w-4 h-4 text-emerald-600" />;
  if (fileType.includes("pdf"))
    return <FileText className="w-4 h-4 text-red-600" />;
  return <FileIcon className="w-4 h-4 text-gray-600" />;
}

function createNewChatSnapshot(count, id) {
  return {
    id: id || Date.now(),
    title: `Chat ${count + 1}`,
    timestamp: new Date().toISOString(),
    messages: [],
  };
}

function formatChatDate(timestamp) {
  if (!timestamp) return "Date unavailable";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString();
}

// ==================== CopyButton ====================

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="hover:text-white hover:bg-gray-700 px-2 py-1 rounded transition-colors text-xs flex items-center space-x-1"
      title={copied ? "Copied!" : "Copy code"}
    >
      <Copy className="w-3.5 h-3.5" />
      <span>{copied ? "Copied!" : "Copy"}</span>
    </button>
  );
}

// ==================== Markdown renderer (memoised) ====================

const markdownComponents = {
  table({ children }) {
    return (
      <div className="overflow-x-auto my-3">
        <table className="min-w-full border-collapse text-sm">
          {children}
        </table>
      </div>
    );
  },
  thead({ children }) {
    return (
      <thead className="bg-gray-700 text-gray-100 dark:bg-gray-600">
        {children}
      </thead>
    );
  },
  th({ children }) {
    return (
      <th className="px-4 py-2 text-left font-semibold border border-gray-600 whitespace-nowrap">
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td className="px-4 py-2 border border-gray-600 align-top">
        {children}
      </td>
    );
  },
  tr({ children }) {
    return (
      <tr className="even:bg-gray-800/30 dark:even:bg-gray-700/30">
        {children}
      </tr>
    );
  },
  code({ node, inline, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : "plaintext";

    const languageNames = {
      python: "Python",
      javascript: "JavaScript",
      typescript: "TypeScript",
      jsx: "JSX",
      tsx: "TSX",
      java: "Java",
      csharp: "C#",
      cpp: "C++",
      c: "C",
      rust: "Rust",
      go: "Go",
      ruby: "Ruby",
      php: "PHP",
      swift: "Swift",
      kotlin: "Kotlin",
      html: "HTML",
      css: "CSS",
      scss: "SCSS",
      sass: "Sass",
      less: "Less",
      sql: "SQL",
      mysql: "MySQL",
      postgresql: "PostgreSQL",
      mongodb: "MongoDB",
      bash: "Bash",
      shell: "Shell",
      zsh: "Zsh",
      json: "JSON",
      yaml: "YAML",
      toml: "TOML",
      xml: "XML",
      markdown: "Markdown",
      dockerfile: "Dockerfile",
      hcl: "HCL",
      terraform: "Terraform",
      bicep: "Bicep",
      arm: "ARM",
      powershell: "PowerShell",
      plaintext: "Text",
      text: "Text",
    };

    const displayName =
      languageNames[language] ||
      language.charAt(0).toUpperCase() + language.slice(1);

    if (!inline && match) {
      return (
        <div className="relative bg-gray-900 rounded-lg overflow-hidden my-3 border border-gray-700">
          <div className="flex items-center justify-between bg-gray-800 px-4 py-2 text-xs text-gray-300 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="font-semibold">{displayName}</span>
            </div>
            <CopyButton text={String(children).replace(/\n$/, "")} />
          </div>
          <pre className="overflow-x-auto p-4 text-gray-100 font-mono text-sm leading-relaxed">
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        </div>
      );
    }

    return (
      <code
        className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-red-600 dark:text-red-400 font-mono text-sm"
        {...props}
      >
        {children}
      </code>
    );
  },
};

// Re-renders only when text changes; prevents remount on every streaming chunk
const MarkdownMessage = React.memo(({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeHighlight]}
    components={markdownComponents}
  >
    {text}
  </ReactMarkdown>
));

// ==================== Main Component ====================

const ChatUI = ({
  apiBase = "https://ask-api.kewt-d.co.uk",
  initialTheme = "light",
}) => {
  const {
    user,
    backendUser,
    preferences,
    isAuthenticated,
    isLoading,
    authError,
    logout,
    getAccessToken,
    updatePreferences,
  } = useAuth();

  // Use theme from backend preferences, with localStorage as fallback
  const [localTheme, setLocalTheme] = useLocalStorage(
    STORAGE_KEYS.theme,
    initialTheme,
  );
  const theme = preferences?.theme || localTheme;
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useLocalStorage(
    STORAGE_KEYS.activeChatId,
    null,
  );
  const [isLoadingChats, setIsLoadingChats] = useState(false);

  const activeChat = useMemo(() => {
    if (chats.length === 0) return null;
    return chats.find((c) => c.id === activeChatId) || chats[0];
  }, [chats, activeChatId]);

  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const userMenuRef = useRef(null);
  const chatChannelRef = useRef(null);
  const abortRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [activeChat?.messages, scrollToBottom]);

  // Load chats from backend when user is authenticated
  useEffect(() => {
    const loadChats = async () => {
      // Only load if user is authenticated and not currently loading
      // CRITICAL: Also check that backendUser exists to ensure backend token is ready
      if (!user?.id || isLoading || !isAuthenticated || !backendUser) {
        console.log(
          "⏳ Waiting for full authentication before loading chats...",
          {
            hasUser: !!user?.id,
            isLoading,
            isAuthenticated,
            hasBackendUser: !!backendUser,
          },
        );
        return;
      }

      try {
        console.log("📂 Loading user chats from backend...");
        setIsLoadingChats(true);
        const userChats = await api.getUserChats(user.id);

        // Transform API response to local format
        const formattedChats = userChats.map((chat) => ({
          id: chat.id || chat.chat_id,
          title: chat.title,
          timestamp: chat.created_at || chat.updated_at || null,
          messages: [], // Messages loaded on-demand when chat is selected
        }));

        setChats(formattedChats);

        // Clear activeChatId if it doesn't exist in backend (e.g., DB was cleared)
        setActiveChatId((currentId) => {
          if (currentId && !formattedChats.some((c) => c.id === currentId)) {
            console.log(
              "⚠️ Active chat no longer exists in backend, clearing:",
              currentId,
            );
            return null;
          }
          return currentId;
        });

        console.log(`✅ Loaded ${formattedChats.length} chats from backend`);
      } catch (error) {
        console.error("❌ Failed to load chats:", error);
        // On error, clear chats and activeChatId to prevent stale state
        setChats([]);
        setActiveChatId(null);
      } finally {
        setIsLoadingChats(false);
      }
    };

    loadChats();
  }, [user?.id, isLoading, isAuthenticated, backendUser]);

  // Cross-tab message synchronization
  useEffect(() => {
    const channel = new BroadcastChannel("chat_channel");
    chatChannelRef.current = channel;

    const handleChatMessage = (event) => {
      const { type, data } = event.data;

      switch (type) {
        case "NEW_MESSAGE":
          // Another tab sent a message - add it to the chat
          if (data.chatId && data.message) {
            setChats((prevChats) =>
              prevChats.map((chat) =>
                chat.id === data.chatId
                  ? {
                      ...chat,
                      messages: [...(chat.messages || []), data.message],
                    }
                  : chat,
              ),
            );
            console.log("Cross-tab: Synced new message");
          }
          break;

        case "NEW_CHAT":
          // Another tab created a chat - add it to the list
          if (data.chat) {
            setChats((prevChats) => {
              // Don't add if already exists
              if (prevChats.some((c) => c.id === data.chat.id))
                return prevChats;
              console.log("Cross-tab: Synced new chat");
              return [data.chat, ...prevChats];
            });
          }
          break;

        case "DELETE_CHAT":
          // Another tab deleted a chat - remove it
          if (data.chatId) {
            setChats((prevChats) =>
              prevChats.filter((c) => c.id !== data.chatId),
            );
            if (activeChatId === data.chatId) {
              setActiveChatId(null);
            }
            console.log("Cross-tab: Synced chat deletion");
          }
          break;

        case "UPDATE_CHAT":
          // Another tab updated a chat - sync the changes
          if (data.chatId && data.updates) {
            setChats((prevChats) =>
              prevChats.map((chat) =>
                chat.id === data.chatId ? { ...chat, ...data.updates } : chat,
              ),
            );
            console.log("Cross-tab: Synced chat update");
          }
          break;
      }
    };

    channel.addEventListener("message", handleChatMessage);

    return () => {
      channel.removeEventListener("message", handleChatMessage);
      channel.close();
    };
  }, [activeChatId]);

  // Cross-tab logout detection - clear chats when user logs out
  useEffect(() => {
    const authChannel = new BroadcastChannel("auth_channel");

    const handleAuthMessage = (event) => {
      const { type } = event.data;

      if (type === "LOGOUT") {
        console.log("Cross-tab: Logout detected, clearing chats");
        setChats([]);
        setActiveChatId(null);
        setInputValue("");
        setAttachedFiles([]);
        setIsTyping(false);
      }
    };

    authChannel.addEventListener("message", handleAuthMessage);

    return () => {
      authChannel.removeEventListener("message", handleAuthMessage);
      authChannel.close();
    };
  }, [setActiveChatId]);

  // Load messages for the active chat
  useEffect(() => {
    const loadChatMessages = async () => {
      if (!activeChatId) return;

      // Skip loading for temporary chats (they're not in the backend yet)
      if (
        typeof activeChatId === "string" &&
        activeChatId.startsWith("temp_")
      ) {
        return;
      }

      // Check if this chat already has messages (e.g., just created and sending first message)
      const currentChat = chats.find((c) => c.id === activeChatId);
      if (currentChat && currentChat.messages.length > 0) {
        console.log("Chat already has messages, skipping load:", activeChatId);
        return;
      }

      try {
        console.log("Loading messages for chat:", activeChatId);
        const messages = await api.getChatMessages(activeChatId, user.id);

        // Transform API messages to local format
        const formattedMessages = messages.map((msg) => ({
          id: msg.message_id,
          // Keep persisted markdown untouched so refresh uses the same render path as streaming.
          text: msg.content || "",
          sender: msg.role === "user" ? "user" : "bot",
          timestamp: msg.created_at,
          status: "delivered",
          // Include response_time if available (for bot messages)
          ...(msg.response_time && msg.role === "assistant"
            ? {
                processingTime: `${msg.response_time.toFixed(2)}s`,
              }
            : {}),
        }));

        // Update the chat with loaded messages
        setChats((prev) =>
          prev.map((c) =>
            c.id === activeChatId ? { ...c, messages: formattedMessages } : c,
          ),
        );
        console.log(
          "Loaded messages for chat:",
          activeChatId,
          formattedMessages,
        );
      } catch (error) {
        console.error("Failed to load messages for chat:", error);

        // If chat doesn't exist on backend (404 or similar), remove it from local state
        if (error.response?.status === 404 || error.message?.includes("404")) {
          console.log(
            "Chat not found on backend, removing from local state:",
            activeChatId,
          );
          setChats((prev) => prev.filter((c) => c.id !== activeChatId));
          setActiveChatId(null);
        }
      }
    };

    loadChatMessages();
  }, [activeChatId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Auto-update chat title based on first message
  useEffect(() => {
    const updateChatTitle = async () => {
      if (
        activeChat &&
        activeChat.messages.length > 0 &&
        activeChat.title.startsWith("Chat ")
      ) {
        const firstUserMessage = activeChat.messages.find(
          (m) => m.sender === "user",
        );
        if (firstUserMessage) {
          const newTitle =
            firstUserMessage.text.slice(0, 30) +
            (firstUserMessage.text.length > 30 ? "..." : "");

          // Update local state immediately
          setChats((prev) =>
            prev.map((c) =>
              c.id === activeChat.id ? { ...c, title: newTitle } : c,
            ),
          );

          // Update backend (fire and forget, don't wait)
          try {
            await api.updateChat(activeChat.id, user?.id, newTitle);
            console.log("✏️ Updated chat title in backend:", {
              chatId: activeChat.id,
              title: newTitle,
            });
          } catch (error) {
            console.error("Failed to update chat title in backend:", error);
          }
        }
      }
    };

    updateChatTitle();
  }, [
    activeChat?.messages.length,
    activeChat?.id,
    activeChat?.title,
    user?.id,
  ]);

  const visibleChats = useMemo(() => {
    return chats
      .map((c) => ({ ...c, active: c.id === activeChat?.id }))
      .filter((chat) =>
        chat.title.toLowerCase().includes(searchQuery.toLowerCase()),
      );
  }, [chats, activeChat, searchQuery]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // reset height
      // set height to scrollHeight capped at max 140px
      textareaRef.current.style.height =
        Math.min(textareaRef.current.scrollHeight, 140) + "px";
    }
  }, [inputValue]);

  /**
   * Effect: Auto-resize textarea based on content
   * Expands textarea height up to maxHeight, then enables scrolling
   */
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height
      const scrollHeight = textareaRef.current.scrollHeight;

      // If content exceeds maxHeight (140px), set to max and enable scrolling
      if (scrollHeight > 140) {
        textareaRef.current.style.height = "140px";
        textareaRef.current.style.overflowY = "auto"; // Enable vertical scrolling
      } else {
        textareaRef.current.style.height = scrollHeight + "px";
        textareaRef.current.style.overflowY = "hidden"; // Hide scrollbar when not needed
      }
    }
  }, [inputValue]);

  const displayName = isLoading ? "Loading..." : user?.name;
  const isNameLong = (displayName || "").length > 18;
  const isNameVeryLong = (displayName || "").length > 28;
  const userNameTextClass = isNameVeryLong
    ? "text-xs"
    : isNameLong
      ? "text-sm"
      : "text-sm";
  const userButtonPadding = isNameVeryLong ? "px-2 py-2" : "px-4 py-2";
  const userNameWidthClass = isNameVeryLong
    ? "max-w-[120px]"
    : isNameLong
      ? "max-w-[160px]"
      : "max-w-[200px]";
  const userMenuWidthClass = isNameLong ? "w-72" : "w-56";

  const bgClass = theme === "dark" ? "bg-gray-900" : "bg-gray-100";
  const cardBgClass = theme === "dark" ? "bg-gray-800" : "bg-white";
  const textClass = theme === "dark" ? "text-gray-100" : "text-gray-900";
  const borderClass = theme === "dark" ? "border-gray-700" : "border-gray-200";

  const handleToggleTheme = async () => {
    const newTheme = theme === "light" ? "dark" : "light";

    // Update localStorage as fallback
    setLocalTheme(newTheme);

    // Save to backend via AuthContext
    if (updatePreferences) {
      try {
        await updatePreferences({ theme: newTheme });
        console.log(`✅ Theme preference saved: ${newTheme}`);
      } catch (error) {
        console.error("Failed to save theme preference:", error);
        // Theme is still saved locally, so user experience is not affected
      }
    }
  };

  const handleLogout = () => {
    // Simple wrapper to call logout - AuthContext handles all safety checks
    logout();
  };

  const handleCreateChat = () => {
    // Create a temporary local-only chat (not saved to DB until first message is sent)
    const tempId = `temp_${Date.now()}`;
    const newChat = {
      id: tempId,
      title: `Chat ${chats.length + 1}`,
      timestamp: new Date().toISOString(),
      messages: [],
      isTemporary: true, // Flag to indicate this chat is not yet saved to DB
    };

    setChats((prev) => [...prev, newChat]);
    setActiveChatId(tempId);
    console.log("Created temporary chat (not saved to DB):", tempId);
  };

  const switchChat = (chatId) => {
    // Clean up empty temporary chats when switching away
    const currentChat = chats.find((c) => c.id === activeChatId);
    if (
      currentChat?.isTemporary &&
      currentChat.messages.length === 0 &&
      activeChatId !== chatId
    ) {
      setChats((prev) => prev.filter((c) => c.id !== activeChatId));
      console.log("Removed empty temporary chat:", activeChatId);
    }

    setActiveChatId(chatId);
    console.log("Switched to chat:", chatId);
  };

  const deleteChat = async (chatId) => {
    // Check if it's a temporary chat (not saved to DB)
    const chatToDelete = chats.find((c) => c.id === chatId);
    const isTemporary = chatToDelete?.isTemporary === true;

    // OPTIMISTIC UPDATE: Delete from UI immediately for better UX
    setChats((prev) => {
      const next = prev.filter((c) => c.id !== chatId);
      if (next.length === 0) {
        setActiveChatId(null);
        return [];
      }
      if (activeChatId === chatId) {
        setActiveChatId(next[0].id);
      }
      return next;
    });
    console.log(
      isTemporary
        ? "Deleted temporary chat (UI):"
        : "Deleting chat from UI (backend async):",
      chatId,
    );

    // Broadcast chat deletion to other tabs
    if (!isTemporary && chatChannelRef.current) {
      chatChannelRef.current.postMessage({
        type: "DELETE_CHAT",
        data: { chatId },
      });
      console.log("Cross-tab: Broadcasted chat deletion");
    }

    // Delete from backend asynchronously (don't await)
    if (!isTemporary) {
      api
        .deleteChat(chatId, user?.id)
        .then(() => {
          console.log("✅ Chat deleted from backend:", chatId);
        })
        .catch((apiError) => {
          // Handle 404 gracefully (chat already deleted)
          if (apiError.response?.status === 404) {
            console.log("Chat already deleted from backend:", chatId);
          } else {
            console.error("❌ Failed to delete chat from backend:", apiError);
            // Optionally: Show a toast notification to user
            // For now, the chat is already removed from UI, so user won't notice
          }
        });
    }

    // Close the confirmation modal
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const handleDeleteClick = (chatId) => {
    setChatToDelete(chatId);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  };

  const validateFile = (file) => {
    const sizeOk = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;
    const typeOk = ACCEPTED_TYPES.some(
      (t) => file.type.startsWith(t) || file.type === t,
    );
    return { sizeOk, typeOk };
  };

  const handleFileUpload = async (files) => {
    const incoming = Array.from(files).map((file) => {
      const { sizeOk, typeOk } = validateFile(file);
      return {
        id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadStatus: sizeOk && typeOk ? "pending" : "error",
        error: !sizeOk
          ? `Max ${MAX_FILE_SIZE_MB}MB`
          : !typeOk
            ? "Unsupported type"
            : null,
      };
    });
    setAttachedFiles((prev) => [...prev, ...incoming]);

    for (const f of incoming) {
      if (f.uploadStatus === "error") continue;
      try {
        // Use the API method to upload the attachment
        const response = await api.uploadAttachment(
          activeChatId?.toString() || "temp",
          f.file,
        );

        console.log("File uploaded successfully:", response);

        setAttachedFiles((prev) =>
          prev.map((x) =>
            x.id === f.id
              ? { ...x, uploadStatus: "completed", error: null }
              : x,
          ),
        );
      } catch (e) {
        console.error("File upload error:", e);
        setAttachedFiles((prev) =>
          prev.map((x) =>
            x.id === f.id
              ? { ...x, uploadStatus: "error", error: "Upload failed" }
              : x,
          ),
        );
      }
    }
  };

  const removeFile = (fileId) =>
    setAttachedFiles((prev) => prev.filter((f) => f.id !== fileId));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileUpload(files);
  };

  const appendMessage = (chatId, message) => {
    setChats((prev) =>
      prev.map((c) =>
        c.id === chatId ? { ...c, messages: [...c.messages, message] } : c,
      ),
    );
  };

  const updateLastBotMessage = (chatId, updater) => {
    setChats((prev) =>
      prev.map((c) => {
        if (c.id !== chatId) return c;
        const messages = c.messages.slice();
        for (let i = messages.length - 1; i >= 0; i--) {
          if ((messages[i].sender ?? messages[i].role) === "bot") {
            messages[i] = updater(messages[i]);
            break;
          }
        }
        return { ...c, messages };
      }),
    );
  };

  const sendMessageToBackend = async (chatId, messageText) => {
    setIsTyping(true);

    const botSeed = {
      id: `${Date.now()}_b`,
      text: "",
      sender: "bot",
      timestamp: new Date().toISOString(),
      status: "delivered",
    };
    appendMessage(chatId, botSeed);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getAccessToken?.();
      if (!token) {
        throw new Error("No access token available");
      }

      if (!user?.id) {
        throw new Error("User ID not available");
      }

      console.log("Sending request to backend...");
      console.log("Chat ID:", chatId);
      console.log("User ID:", user.id);
      console.log("User message:", messageText);

      const startTime = Date.now();
      let accumulatedText = "";

      // Stream chunks to UI for real-time display
      // Throttle: coalesce rapid chunk arrivals into one render per ~50 ms (~20 fps)
      let rafId = null;
      const onChunk = (chunk) => {
        accumulatedText += chunk;
        if (rafId !== null) clearTimeout(rafId);
        rafId = setTimeout(() => {
          rafId = null;
          // Update the bot message with raw streamed markdown
          updateLastBotMessage(chatId, (m) => ({
            ...m,
            text: accumulatedText,
            isStreaming: true,
          }));
        }, 50);
      };

      const data = await api.getAIResponse(
        chatId,
        user.id,
        messageText,
        onChunk,
      );
      console.log("Response data:", data);

      if (!data.reply) {
        throw new Error("No reply in response data");
      }

      // Calculate response time once at the beginning
      const responseTimeSeconds = (Date.now() - startTime) / 1000;
      const processingTime = `${responseTimeSeconds.toFixed(2)}s`;

      // If backend sent formatted version directly, use it
      if (data.isFormatted) {
        console.log(
          "✅ Using formatted response from backend (no DB reload needed)",
        );

        // Save response time to database
        if (data.messageId) {
          try {
            await api.updateMessageResponseTime(
              data.messageId,
              chatId,
              user.id,
              responseTimeSeconds,
            );
            console.log("✅ Saved response time to DB:", processingTime);
          } catch (error) {
            console.error("Failed to save response time:", error);
          }
        }

        // Update with formatted response directly from backend
        updateLastBotMessage(chatId, (m) => ({
          ...m,
          text: data.reply,
          isStreaming: false,
          processingTime,
        }));
        setIsTyping(false);
        abortRef.current = null;

        // Broadcast message to other tabs
        if (chatChannelRef.current) {
          const botMessage = {
            id: botSeed.id,
            text: data.reply,
            sender: "bot",
            timestamp: new Date().toISOString(),
            status: "delivered",
            processingTime,
          };
          chatChannelRef.current.postMessage({
            type: "NEW_MESSAGE",
            data: { chatId, message: botMessage },
          });
          console.log("Cross-tab: Broadcasted formatted bot message");
        }

        return;
      }

      // Legacy fallback: reload from database if needed
      if (data.shouldReload) {
        console.log("🔄 Reloading formatted response from database...");
        // Wait a moment for database to be updated
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Reload messages for this chat
        try {
          const messages = await api.getChatMessages(chatId, user.id);
          const lastBotMessage = messages
            .reverse()
            .find((m) => m.role === "assistant");
          if (lastBotMessage) {
            // Always use frontend-calculated time (user-perceived time including network latency)
            console.log(
              "⏱️  Using frontend response time:",
              processingTime,
              `(${responseTimeSeconds.toFixed(2)}s)`,
            );

            // Save frontend response time to database (overwrite backend time if exists)
            try {
              await api.updateMessageResponseTime(
                lastBotMessage.message_id,
                chatId,
                user.id,
                responseTimeSeconds,
              );
              console.log(
                "✅ Saved frontend response time to DB:",
                processingTime,
              );
            } catch (error) {
              console.error("Failed to save response time:", error);
            }

            // Update with the formatted version from database
            // Map backend properties to frontend properties
            const reloadedMessage = {
              id: lastBotMessage.message_id || lastBotMessage.id,
              text: lastBotMessage.content,
              sender: "bot",
              timestamp: lastBotMessage.created_at,
              status: "delivered",
              isStreaming: false,
              processingTime, // Use frontend-calculated time
            };
            updateLastBotMessage(chatId, () => reloadedMessage);
            setIsTyping(false);
            abortRef.current = null;

            // Broadcast message to other tabs
            if (chatChannelRef.current) {
              chatChannelRef.current.postMessage({
                type: "NEW_MESSAGE",
                data: { chatId, message: reloadedMessage },
              });
              console.log("Cross-tab: Broadcasted reloaded bot message");
            }

            console.log("✅ Loaded formatted response from database");
            return;
          }
        } catch (error) {
          console.error("Failed to reload formatted response:", error);
          // Fall through to use streamed version
        }
      }

      // Keep the assistant reply as raw markdown so streaming and refreshed views stay identical.
      const finalReply = data.reply;
      console.log("✅ Response received, length:", finalReply.length);

      updateLastBotMessage(chatId, (m) => ({
        ...m,
        text: finalReply,
        isStreaming: false,
        processingTime,
      }));

      setIsTyping(false);
      abortRef.current = null;

      // Broadcast message to other tabs
      if (chatChannelRef.current) {
        const botMessage = {
          id: botSeed.id,
          text: finalReply,
          sender: "bot",
          timestamp: new Date().toISOString(),
          status: "delivered",
          processingTime,
        };
        chatChannelRef.current.postMessage({
          type: "NEW_MESSAGE",
          data: { chatId, message: botMessage },
        });
        console.log("Cross-tab: Broadcasted new bot message");
      }
    } catch (e) {
      setIsTyping(false);
      abortRef.current = null;

      if (e.name === "AbortError") return;

      console.error("Chat error:", e);
      updateLastBotMessage(chatId, (m) => ({
        ...m,
        text: `⚠ Error: ${e.message}`,
        sender: "bot",
      }));
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachedFiles.length === 0) return;

    const userInput = inputValue.trim();
    const now = new Date();

    let targetChatId = activeChatId;
    let isNewChat = false;

    // Check if current chat is temporary (not yet saved to DB)
    const currentChat = chats.find((c) => c.id === activeChatId);
    const isTemporaryChat = currentChat?.isTemporary === true;

    // If no active chat OR chat is temporary, create it on backend
    if (!targetChatId || isTemporaryChat) {
      try {
        // Generate a smart title from the first message
        const smartTitle =
          userInput.slice(0, 30) + (userInput.length > 30 ? "..." : "");

        const requestUserId = backendUser?.user_id || user?.id;
        const backendChat = await api.createChat(requestUserId, smartTitle);

        const createdChatId = backendChat.id || backendChat.chat_id;
        if (!createdChatId) {
          throw new Error("Backend did not return chat id");
        }

        targetChatId = createdChatId;
        isNewChat = true;

        // Create user message first
        const userMsg = {
          id: `${now.getTime()}_u`,
          text: userInput,
          sender: "user",
          timestamp: now.toISOString(),
          status: "delivered",
          files: attachedFiles.length ? attachedFiles.slice() : null,
        };

        // Create the new chat with the user message already included
        const newChat = {
          id: createdChatId,
          title: smartTitle, // Use the smart title based on first message
          timestamp:
            backendChat.created_at ||
            backendChat.updated_at ||
            new Date().toISOString(),
          messages: [userMsg], // Start with the user message
          isTemporary: false, // Now it's saved to DB
        };

        // Replace the temporary chat or add new chat
        if (isTemporaryChat) {
          setChats((prev) =>
            prev.map((c) => (c.id === activeChatId ? newChat : c)),
          );
          console.log(
            "✨ Converted temporary chat to backend chat with smart title:",
            {
              chatId: createdChatId,
              title: smartTitle,
            },
          );
        } else {
          setChats((prev) => [...prev, newChat]);
          console.log("✨ Auto-created chat on backend with smart title:", {
            chatId: createdChatId,
            title: smartTitle,
          });
        }

        setActiveChatId(createdChatId);

        // Broadcast new chat to other tabs
        if (chatChannelRef.current) {
          chatChannelRef.current.postMessage({
            type: "NEW_CHAT",
            data: { chat: newChat },
          });
          console.log("Cross-tab: Broadcasted new chat");
        }

        // Broadcast user message to other tabs
        if (chatChannelRef.current) {
          chatChannelRef.current.postMessage({
            type: "NEW_MESSAGE",
            data: { chatId: targetChatId, message: userMsg },
          });
          console.log("Cross-tab: Broadcasted new user message");
        }

        // Clear input immediately
        setInputValue("");
        setAttachedFiles([]);

        // Send to backend (this will add the bot response)
        await sendMessageToBackend(targetChatId, userInput);
        return; // Early return since we've handled everything
      } catch (error) {
        console.error("Failed to create chat:", error);
        return;
      }
    }

    // Existing chat - normal flow
    const userMsg = {
      id: `${now.getTime()}_u`,
      text: userInput,
      sender: "user",
      timestamp: now.toISOString(),
      status: "delivered",
      files: attachedFiles.length ? attachedFiles.slice() : null,
    };

    appendMessage(targetChatId, userMsg);
    setInputValue("");
    setAttachedFiles([]);

    // Broadcast user message to other tabs
    if (chatChannelRef.current) {
      chatChannelRef.current.postMessage({
        type: "NEW_MESSAGE",
        data: { chatId: targetChatId, message: userMsg },
      });
      console.log("Cross-tab: Broadcasted user message");
    }

    await sendMessageToBackend(targetChatId, userInput);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const exportChat = (chatId) => {
    const chat = chats.find((c) => c.id === chatId);
    if (!chat) return;

    const exportData = {
      title: chat.title,
      timestamp: chat.timestamp,
      messages: chat.messages.map((m) => ({
        sender: m.sender,
        text: m.text,
        timestamp: m.timestamp,
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${chat.title.replace(/[^a-z0-9]/gi, "_")}_${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-screen ${bgClass}`}>
      <div
        className={`w-64 ${cardBgClass} border-r ${borderClass} flex flex-col`}
      >
        <div className="flex justify-center p-3">
          <img
            src="/KPMG.svg"
            alt="Company Logo"
            className="h-10 w-auto transition-all duration-200"
            style={{
              // Keep original colours in light theme; in dark theme force white
              filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
            }}
          />
        </div>
        <div className="p-4">
          <button
            onClick={handleCreateChat}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-700 dark:from-blue-500 dark:to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-800 dark:hover:from-blue-600 dark:hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">New Chat</span>
          </button>
        </div>

        <div className="px-4 pb-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search chats..."
            className={`w-full px-3 py-2 rounded-md text-sm outline-none border ${
              theme === "dark"
                ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                : "bg-white border-gray-200 text-gray-900 placeholder-gray-500"
            } focus:ring-2 focus:ring-blue-500`}
            aria-label="Search chats"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <h3
            className={`text-xs font-semibold ${
              theme === "dark" ? "text-gray-400" : "text-gray-500"
            } uppercase tracking-wide mb-3`}
          >
            Chat History
          </h3>
          <div className="space-y-2">
            {isLoadingChats ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                <p
                  className={`text-sm ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                >
                  Loading chats...
                </p>
              </div>
            ) : (
              <>
                {visibleChats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => switchChat(chat.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        switchChat(chat.id);
                      }
                    }}
                    className={`group w-full flex items-start space-x-3 px-3 py-2 rounded-lg transition-all duration-200 text-left border cursor-pointer ${
                      chat.active
                        ? theme === "dark"
                          ? "bg-gray-800 border-blue-500/30 hover:bg-gray-700 ring-1 ring-inset ring-blue-400"
                          : "bg-blue-50 border-blue-200 hover:bg-blue-100 ring-1 ring-inset ring-blue-300"
                        : theme === "dark"
                          ? "border-gray-700 hover:bg-gray-600"
                          : "border-blue-200 hover:bg-blue-100"
                    }`}
                  >
                    <MessageSquare
                      className={`w-4 h-4 mt-1 flex-shrink-0 ${
                        chat.active
                          ? "text-blue-600"
                          : theme === "dark"
                            ? "text-gray-400"
                            : "text-gray-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium truncate ${
                          chat.active ? "text-blue-600" : textClass
                        }`}
                      >
                        {chat.title}
                      </p>
                      <p
                        className={`text-xs ${
                          theme === "dark" ? "text-gray-500" : "text-gray-500"
                        }`}
                      >
                        {formatChatDate(chat.timestamp)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(chat.id);
                      }}
                      title="Delete chat"
                      className={`p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ${
                        theme === "dark"
                          ? "text-gray-400 hover:text-red-400 hover:bg-gray-600"
                          : "text-gray-400 hover:text-red-600 hover:bg-gray-100"
                      }`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {visibleChats.length === 0 && !isLoadingChats && (
                  <div
                    className={`${
                      theme === "dark" ? "text-gray-400" : "text-gray-500"
                    } text-sm px-1 py-2`}
                  >
                    No chats found.
                    {searchQuery && (
                      <button
                        className="ml-2 text-blue-600 hover:underline"
                        onClick={() => setSearchQuery("")}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className={`${cardBgClass} shadow-sm border-b ${borderClass}`}>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <img
                    src="/logo_new.png"
                    alt="KPMG"
                    className="w-16 h-16 object-contain"
                  />
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>  
                <div>
                  <h1 className={`text-xl font-bold ${textClass}`}>
                    Gordon - Dev
                  </h1>
                  <div
                    className={`flex items-center space-x-4 text-sm ${
                      theme === "dark" ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    <span className="flex items-center space-x-1">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:ext-green-400" />
                      <span>Online</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className={`flex items-center space-x-3 ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-50"
                  } ${userButtonPadding} rounded-lg hover:shadow-md transition-all`}
                  aria-haspopup="menu"
                  aria-expanded={showUserMenu}
                >
                  <div className="flex items-center space-x-2">
                    {user?.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user?.name || "User"}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className={`hidden sm:block text-left w-25`}>
                      <div
                        className={`${userNameTextClass} font-medium ${textClass} truncate`}
                      >
                        {displayName}
                      </div>
                      <div
                        className={`text-xs ${
                          theme === "dark" ? "text-gray-400" : "text-gray-500"
                        } truncate`}
                      >
                        {user?.email}
                      </div>
                    </div>
                  </div>
                </button>

                {showUserMenu && (
                  <div
                    className={`absolute right-0 mt-2 ${userMenuWidthClass} ${cardBgClass} rounded-lg shadow-xl border ${borderClass} py-2 z-50`}
                    role="menu"
                  >
                    <div className={`px-4 py-3 border-b ${borderClass}`}>
                      <p
                        className={`text-sm font-medium ${textClass} truncate`}
                      >
                        {displayName}
                      </p>
                      <p
                        className={`text-xs ${
                          theme === "dark" ? "text-gray-400" : "text-gray-500"
                        } truncate`}
                      >
                        {user?.email || "No authentication"}
                      </p>
                    </div>
                    <button
                      onClick={handleToggleTheme}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${
                        theme === "dark"
                          ? "text-gray-300 hover:bg-gray-700"
                          : "text-gray-700 hover:bg-gray-50"
                      } transition-colors`}
                      role="menuitem"
                    >
                      {theme === "light" ? (
                        <>
                          <Moon className="w-4 h-4" />
                          <span>Dark Theme</span>
                        </>
                      ) : (
                        <>
                          <Sun className="w-4 h-4" />
                          <span>Light Theme</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleLogout}
                      // className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                      role="menuitem"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Removed error banner - silent auto-logout on token expiration */}

        <div
          className={`flex-1 overflow-y-auto px-6 py-6 space-y-6 ${
            theme === "dark"
              ? "bg-gradient-to-b from-gray-900 to-gray-800"
              : "bg-gradient-to-b from-gray-50 to-white"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDragging && (
            <div className="fixed inset-0 bg-blue-600 bg-opacity-10 backdrop-blur-sm border-2 border-dashed border-blue-400 flex items-center justify-center z-50 transition-all duration-200">
              <div
                className={`${cardBgClass} p-10 rounded-xl shadow-2xl text-center border border-blue-200`}
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Paperclip className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className={`text-xl font-semibold ${textClass} mb-2`}>
                  Drop Enterprise Files Here
                </h3>
                <p
                  className={
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }
                >
                  Supports common document types • Secure upload
                </p>
              </div>
            </div>
          )}

          {activeChat?.messages?.map((message) => (
            <div
              key={message.id}
              className={`flex items-start ${
                message.sender === "user" ? "justify-end" : "justify-start"
              }`}
            >
              {message.sender !== "user" && (
                <div className="mr-3 mt-1 flex-shrink-0">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shadow bg-gradient-to-br from-blue-600 to-purple-700">
                    <Zap className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
              <div
                className={`group relative max-w-3xl ${
                  message.sender === "user" ? "ml-16" : "mr-16"
                }`}
              >
                <div
                  className={`px-6 py-4 rounded-xl shadow-sm transition-all duration-200 hover:shadow-md ${
                    message.sender === "user"
                      ? theme === "dark"
                        ? "bg-emerald-900/40 text-emerald-50 border border-emerald-700/50"
                        : "bg-emerald-50 text-emerald-950 border border-emerald-200"
                      : theme === "dark"
                        ? "bg-blue-900/35 text-blue-50 border border-blue-700/45"
                        : "bg-blue-50 text-blue-950 border border-blue-200"
                  }
                  
                  `}
                >
                  {message.files && (
                    <div className="mb-4 space-y-3">
                      <div className="text-xs font-medium opacity-70 uppercase tracking-wide">
                        Attachments ({message.files.length})
                      </div>
                      {message.files.map((file) => (
                        <div
                          key={file.id}
                          className={`flex items-center space-x-3 p-3 rounded-lg ${
                            message.sender === "user"
                              ? "bg-emerald-100/60"
                              : theme === "dark"
                                ? "bg-gray-700"
                                : "bg-gray-50"
                          }`}
                        >
                          <div className="flex-shrink-0">
                            {getFileIcon(file.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {file.name}
                            </p>
                            <div className="flex items-center space-x-2 text-xs opacity-70">
                              <span>{formatFileSize(file.size)}</span>
                              {file.uploadStatus === "completed" && (
                                <>
                                  <span>•</span>
                                  <CheckCircle className="w-3 h-3" />
                                  <span>Processed</span>
                                </>
                              )}
                              {file.uploadStatus === "error" && (
                                <>
                                  <span>•</span>
                                  <AlertCircle className="w-3 h-3 text-red-500" />
                                  <span className="text-red-600">
                                    {file.error}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            className="flex-shrink-0 p-1 opacity-70 hover:opacity-100 transition-opacity"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-sm leading-relaxed break-words prose prose-sm dark:prose-invert max-w-none [&>*]:mb-3 [&>h1]:mt-4 [&>h2]:mt-4 [&>h3]:mt-3 [&>p]:mb-2 [&>ul]:my-2 [&>ol]:my-2 [&>li]:mb-1">
                    <MarkdownMessage
                      text={typeof message.text === "string" ? message.text : String(message.text || "")}
                    />
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-opacity-20">
                    <div className="flex items-center space-x-3 text-xs">
                      <span className="opacity-70">
                        {new Date(message.timestamp).toLocaleString([], {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                      {message.processingTime && (
                        <>
                          <span className="opacity-50">•</span>
                          <span className="flex items-center space-x-1 opacity-70">
                            <Clock className="w-3 h-3" />
                            <span>{message.processingTime}</span>
                          </span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center space-x-1">
                      {message.sender === "bot" && (
                        <button
                          onClick={(e) => {
                            const plainText = markdownToPlainText(message.text);
                            navigator.clipboard.writeText(plainText);
                            // Optional: Show a success message
                            const button = e.currentTarget;
                            const originalTitle = button.title;
                            button.title = "Copied!";
                            setTimeout(() => {
                              button.title = originalTitle;
                            }, 2000);
                          }}
                          className="p-1 opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity"
                          title="Copy response"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                      <button className="p-1 opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity">
                        <MoreVertical className="w-3 h-3" />
                      </button>
                      {message.status === "delivered" && (
                        <CheckCircle
                          className={`w-4 h-4 ${
                            message.sender === "user"
                              ? "text-emerald-400"
                              : "text-green-500"
                          }`}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {message.sender === "user" && (
                <div className="ml-3 mt-1 flex-shrink-0">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shadow bg-emerald-500">
                    <User className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </div>
          ))}

          {isTyping && (
            <div className="flex justify-start">
              <div className="mr-16 max-w-2xl">
                <div
                  className={`${
                    theme === "dark"
                      ? "bg-gray-800 text-gray-100 border-gray-700"
                      : "bg-white text-gray-800 border-gray-200"
                  } border px-6 py-4 rounded-xl shadow-sm`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.12s" }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                        style={{ animationDelay: "0.24s" }}
                      ></div>
                    </div>
                    <span
                      className={`text-sm ${
                        theme === "dark" ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      AI is analyzing your request...
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {attachedFiles.length > 0 && (
          <div className={`px-6 py-4 ${cardBgClass} border-t ${borderClass}`}>
            <div className="flex items-center justify-between mb-3">
              <h3
                className={`text-sm font-semibold ${
                  theme === "dark" ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Attachments ({attachedFiles.length})
              </h3>
              <button
                onClick={() => setAttachedFiles([])}
                className={`text-xs ${
                  theme === "dark"
                    ? "text-gray-400 hover:text-gray-300"
                    : "text-gray-500 hover:text-gray-700"
                } transition-colors`}
              >
                Clear all
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {attachedFiles.map((file) => (
                <div
                  key={file.id}
                  className={`flex items-center space-x-3 ${
                    theme === "dark" ? "bg-gray-700" : "bg-gray-50"
                  } p-3 rounded-lg border ${borderClass} group hover:shadow-sm transition-all`}
                >
                  <div className="flex-shrink-0">{getFileIcon(file.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${textClass} truncate`}>
                      {file.name}
                    </p>
                    <div
                      className={`flex items-center space-x-2 text-xs ${
                        theme === "dark" ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <span>{formatFileSize(file.size)}</span>
                      {file.uploadStatus === "pending" && (
                        <>
                          <span>•</span>
                          <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          <span>Processing...</span>
                        </>
                      )}
                      {file.uploadStatus === "completed" && (
                        <>
                          <span>•</span>
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          <span className="text-green-600">Ready</span>
                        </>
                      )}
                      {file.uploadStatus === "error" && (
                        <>
                          <span>•</span>
                          <AlertCircle className="w-3 h-3 text-red-500" />
                          <span className="text-red-600">{file.error}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(file.id)}
                    className={`flex-shrink-0 p-1 ${
                      theme === "dark" ? "text-gray-500" : "text-gray-400"
                    } opacity-0 group-hover:opacity-100 hover:text-red-500 transition-all`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`${cardBgClass} border-t ${borderClass} px-6 py-4`}>
          <div className="flex items-center space-x-4">
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => handleFileUpload(e.target.files)}
              multiple
              className="hidden"
              accept={ACCEPTED_TYPES.join(",")}
            />
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`group p-3 ${
                  theme === "dark"
                    ? "text-gray-400 hover:text-blue-400 hover:bg-gray-700"
                    : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                } rounded-lg transition-all duration-200`}
                title="Attach files"
              >
                <Paperclip className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask your enterprise AI assistant anything... (Press Enter to send)"
                rows={1}
                className={`custom-scrollbar-textarea w-full px-4 py-3 border ${
                  theme === "dark"
                    ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                } resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-sm leading-relaxed`}
                style={{ minHeight: "18px", maxHeight: "140px" }}
                aria-label="Message input"
              />
            </div>

            <button
              onClick={handleSendMessage}
              disabled={inputValue.trim() === "" && attachedFiles.length === 0}
              className=" group p-4 bg-gradient-to-r from-blue-600 to-purple-700 text-white  dark:from-blue-500 dark:to-purple-600 hover:from-blue-700 hover:to-purple-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-blue-600 disabled:hover:to-purple-700 shadow-lg hover:shadow-xl "
              title="Send message"
            >
              <Send className="w-6 h-5 transform group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className={`${cardBgClass} rounded-xl shadow-2xl max-w-md w-full p-6 border ${borderClass}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start space-x-4 mb-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className={`text-lg font-semibold ${textClass} mb-2`}>
                  Delete Chat?
                </h3>
                <p
                  className={`text-sm ${
                    theme === "dark" ? "text-gray-400" : "text-gray-600"
                  }`}
                >
                  Are you sure you want to delete this chat? This action cannot
                  be undone and all messages will be permanently removed.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={handleCancelDelete}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  theme === "dark"
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteChat(chatToDelete)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Delete Chat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatUI;
